/**
 * Message handlers - pure functions that transform protocol payloads into state mutations.
 *
 * Each handler receives a payload and returns either:
 * - A partial state object to merge
 * - A function (prevState) => partialState for state that depends on current values
 *
 * This concentrates all protocol-to-state mapping in one place.
 */

import type {
  Project,
  Profile,
  Application,
  AppVersion,
  DeploymentProfileApplication,
  Organization,
  SnapshotStartPayload,
  SnapshotChunkPayload,
  SnapshotEndPayload,
  CustomHostname,
  AppPoolCoverageEntry,
} from "@interlucent/organization-protocol";
import { logger } from '../log';

const log = logger('handler');

// =============================================================================
// STATE TYPES
// =============================================================================

export type StoreState = {
  lastSeq: number;
  rowVersion: number;
  organization: Organization | null;
  projects: Record<string, Project>;
  profiles: Record<string, Profile>;
  applications: Record<string, Application>;
  versions: Record<string, AppVersion>;
  mappings: Record<string, DeploymentProfileApplication>;
  /**
   * Resolver-correct "which pools provision this app-version?" map.
   * Keyed by appId; value is the full list of (poolId, versionId, flags)
   * pairs provisioned for that app across every pool. Written by the
   * snapshot's `application` chunks and updated by AppPoolCoverageChanged
   * events. Empty array = no pool provisions the app.
   *
   * This is the source the UI should read for membership displays —
   * the legacy `mappings` map filters DPA rows by appId, which can
   * include rows that resolve to "not provisioned" (the classic
   * mapped-but-not-provisioned bug).
   */
  appPoolCoverage: Record<string, AppPoolCoverageEntry[]>;
  uploadProgress: Record<string, UploadProgressEntry>;
  customHostnames: Record<string, CustomHostname>;
  snapshotLoading: boolean;
  chunksReceived: number;
  chunksExpected: number;
  loadedTypes: Set<string>;
  loadedApps: Set<string>;
  connectionState: 'connected' | 'disconnected' | 'reconnecting';
  wsEverConnected: boolean;
};

export type UploadProgressEntry = {
  sent: number;
  total: number;
  key: string;
  projectId?: string;
  appId?: string;
  replaceVersionId?: string;
  paused?: boolean;
};

export type StateMutation = Partial<StoreState> | ((s: StoreState) => Partial<StoreState>);

// =============================================================================
// SNAPSHOT HANDLERS
// =============================================================================

export function handleSnapshotStart(payload: SnapshotStartPayload): StateMutation {
  log.debug(`Snapshot starting, expecting ${payload.totalChunks} chunks`);
  return {
    snapshotLoading: true,
    chunksReceived: 0,
    chunksExpected: payload.totalChunks,
    loadedTypes: new Set<string>(),
    loadedApps: new Set<string>(),
    rowVersion: payload.rowVersion,
    organization: null,
    projects: {},
    profiles: {},
    applications: {},
    versions: {},
    mappings: {},
    uploadProgress: {},
    customHostnames: {},
  };
}

export function handleSnapshotChunk(chunk: SnapshotChunkPayload): StateMutation {
  log.debug(`Received chunk: ${chunk.type}`);

  if (chunk.type === "application") {
    const { appId, app, versions, mappings, coveringPools } = chunk;
    return (s) => {
      const newVersions = { ...s.versions };
      (versions || []).forEach((v) => { newVersions[v.id] = v; });
      const newMappings = { ...s.mappings };
      (mappings || []).forEach((m) => { newMappings[m.id] = m; });
      const newCoverage = { ...s.appPoolCoverage };
      // Server sends the authoritative coverage list for this app; replace.
      // Absent field (legacy snapshot) preserves existing entry.
      if (Array.isArray(coveringPools)) newCoverage[appId] = coveringPools;
      return {
        chunksReceived: s.chunksReceived + 1,
        loadedApps: new Set([...s.loadedApps, appId]),
        applications: { ...s.applications, [appId]: app },
        versions: newVersions,
        mappings: newMappings,
        appPoolCoverage: newCoverage,
      };
    };
  }

  if (chunk.type === "organization") {
    return (s) => ({
      chunksReceived: s.chunksReceived + 1,
      loadedTypes: new Set([...s.loadedTypes, "organization"]),
      organization: chunk.data,
    });
  }

  if (chunk.type === "projects") {
    const projects: Record<string, Project> = {};
    (chunk.data || []).forEach((x) => { projects[x.id] = x; });
    return (s) => ({
      chunksReceived: s.chunksReceived + 1,
      loadedTypes: new Set([...s.loadedTypes, "projects"]),
      projects,
    });
  }

  if (chunk.type === "profiles") {
    const profiles: Record<string, Profile> = {};
    (chunk.data || []).forEach((x) => { profiles[x.id] = x; });
    return (s) => ({
      chunksReceived: s.chunksReceived + 1,
      loadedTypes: new Set([...s.loadedTypes, "profiles"]),
      profiles,
    });
  }

  if (chunk.type === "uploads") {
    const uploadProgress: Record<string, UploadProgressEntry> = {};
    (chunk.data || []).forEach((upload) => {
      const uploadId = upload.uploadId;
      if (uploadId) {
        uploadProgress[uploadId] = {
          sent: upload.sentBytes || 0,
          total: upload.totalBytes || upload.size || 0,
          key: upload.key,
          projectId: upload.projectId,
          appId: upload.appId,
          replaceVersionId: upload.replaceVersionId,
          paused: upload.state === 'paused'
        };
      }
    });
    return (s) => ({
      chunksReceived: s.chunksReceived + 1,
      loadedTypes: new Set([...s.loadedTypes, "uploads"]),
      uploadProgress,
    });
  }

  if (chunk.type === "customHostnames") {
    const customHostnames: Record<string, CustomHostname> = {};
    (chunk.data || []).forEach((h) => { customHostnames[h.id] = h; });
    return (s) => ({
      chunksReceived: s.chunksReceived + 1,
      loadedTypes: new Set([...s.loadedTypes, "customHostnames"]),
      customHostnames,
    });
  }

  // Unknown chunk type - no-op
  return {};
}

export function handleSnapshotEnd(payload: SnapshotEndPayload): StateMutation {
  log.debug(`Snapshot complete, seq: ${payload.seq}`);
  return {
    snapshotLoading: false,
    lastSeq: payload.seq,
  };
}

// =============================================================================
// PROJECT EVENT HANDLERS
// =============================================================================

export function handleProjectUpsert(payload: Project): StateMutation {
  return (s) => ({
    projects: { ...s.projects, [payload.id]: { ...(s.projects[payload.id] || {}), ...payload } }
  });
}

export function handleProjectDeleted(payload: { id: string }): StateMutation {
  log.debug(`ProjectDeleted: ${payload.id}`);
  return (s) => {
    const newProjects = { ...s.projects };
    delete newProjects[payload.id];
    return { projects: newProjects };
  };
}

// =============================================================================
// PROFILE EVENT HANDLERS
// =============================================================================

export function handleProfileUpsert(payload: Profile): StateMutation {
  return (s) => ({
    profiles: { ...s.profiles, [payload.id]: { ...(s.profiles[payload.id] || {}), ...payload } }
  });
}

export function handleProfileCreated(payload: Profile): StateMutation {
  log.debug(`Profile created: ${payload.id}`);
  return (s) => ({
    profiles: { ...s.profiles, [payload.id]: payload }
  });
}

export function handleProfileDeleted(payload: { id: string }): StateMutation {
  log.debug(`Profile deleted: ${payload.id}`);
  return (s) => {
    const existing = s.profiles[payload.id];
    if (!existing) return s;
    return {
      profiles: {
        ...s.profiles,
        [payload.id]: { ...existing, deletedAt: new Date().toISOString() },
      },
    };
  };
}

export function handleProfileActivated(payload: { id: string }): StateMutation {
  log.debug(`Profile activated: ${payload.id}`);
  return (s) => ({
    profiles: {
      ...s.profiles,
      [payload.id]: { ...(s.profiles[payload.id] || {} as Profile), active: true }
    }
  });
}

export function handleProfileDeactivated(payload: { id: string }): StateMutation {
  log.debug(`Profile deactivated: ${payload.id}`);
  return (s) => ({
    profiles: {
      ...s.profiles,
      [payload.id]: { ...(s.profiles[payload.id] || {} as Profile), active: false }
    }
  });
}

// =============================================================================
// APPLICATION EVENT HANDLERS
// =============================================================================

export function handleApplicationCreated(payload: Application): StateMutation {
  return (s) => ({
    applications: { ...s.applications, [payload.id]: payload },
    loadedApps: new Set([...s.loadedApps, payload.id]),
  });
}

export function handleApplicationDeleted(payload: { id: string; severedProfileIds?: string[] }): StateMutation {
  return (s) => {
    const newApplications = { ...s.applications };
    delete newApplications[payload.id];
    const newLoadedApps = new Set(s.loadedApps);
    newLoadedApps.delete(payload.id);
    // Remove all profile-app mappings that referenced this app
    const newMappings = { ...s.mappings };
    for (const [mappingId, mapping] of Object.entries(newMappings)) {
      if (mapping.appId === payload.id) {
        delete newMappings[mappingId];
      }
    }
    return { applications: newApplications, loadedApps: newLoadedApps, mappings: newMappings };
  };
}

export function handleApplicationUpdated(payload: Application): StateMutation {
  return (s) => ({
    applications: { ...s.applications, [payload.id]: { ...(s.applications[payload.id] || {}), ...payload } }
  });
}

export function handleApplicationMoved(payload: {
  id: string;
  fromProjectId: string;
  toProjectId: string;
  appSlug: string;
  rowVersion: number;
  updatedAt: string;
  removedMappingIds?: string[];
}): StateMutation {
  log.debug(`Application moved: ${payload.id} ${payload.fromProjectId} -> ${payload.toProjectId}`);
  return (s) => {
    const existing = s.applications[payload.id];
    if (!existing) return {};
    const newMappings = { ...s.mappings };
    for (const mappingId of payload.removedMappingIds || []) {
      delete newMappings[mappingId];
    }
    return {
      applications: {
        ...s.applications,
        [payload.id]: {
          ...existing,
          projectId: payload.toProjectId,
          appSlug: payload.appSlug,
          rowVersion: payload.rowVersion,
          updatedAt: payload.updatedAt,
        },
      },
      mappings: newMappings,
    };
  };
}

// =============================================================================
// VERSION EVENT HANDLERS
// =============================================================================

export function handleVersionCreated(payload: AppVersion): StateMutation {
  return (s) => ({
    versions: { ...s.versions, [payload.id]: payload }
  });
}

export function handleVersionDeleted(payload: { id: string }): StateMutation {
  return (s) => {
    const newVersions = { ...s.versions };
    delete newVersions[payload.id];
    return { versions: newVersions };
  };
}

export function handleVersionUpdated(payload: Partial<AppVersion> & { id: string }): StateMutation {
  log.debug(`Version updated: ${payload.id}`);
  return (s) => ({
    versions: {
      ...s.versions,
      [payload.id]: { ...(s.versions[payload.id] || {}), ...payload }
    }
  });
}

// =============================================================================
// LOCAL VERSION EVENT HANDLERS
// =============================================================================

export function handleLocalVersionCreated(payload: AppVersion): StateMutation {
  log.debug(`Local version created: ${payload.id}`);
  return (s) => ({
    versions: { ...s.versions, [payload.id]: payload }
  });
}

export function handleLocalVersionUpdated(payload: Partial<AppVersion> & { id: string }): StateMutation {
  log.debug(`Local version updated: ${payload.id}`);
  return (s) => ({
    versions: {
      ...s.versions,
      [payload.id]: { ...(s.versions[payload.id] || {}), ...payload }
    }
  });
}

export function handleLocalVersionDeleted(payload: { id: string }): StateMutation {
  log.debug(`Local version deleted: ${payload.id}`);
  return (s) => {
    const newVersions = { ...s.versions };
    delete newVersions[payload.id];
    return { versions: newVersions };
  };
}

export function handleLocalVersionTokensCycled(payload: { id: string; localTokensGeneratedAt: string }): StateMutation {
  log.debug(`Local version tokens cycled: ${payload.id}`);
  return (s) => ({
    versions: {
      ...s.versions,
      [payload.id]: { ...(s.versions[payload.id] || {}), localTokensGeneratedAt: payload.localTokensGeneratedAt }
    }
  });
}

// =============================================================================
// MAPPING EVENT HANDLERS
// =============================================================================

export function handleMappingUpserted(payload: DeploymentProfileApplication): StateMutation {
  return (s) => ({
    mappings: { ...s.mappings, [payload.id]: payload }
  });
}

export function handleMappingDeleted(payload: { id: string }): StateMutation {
  return (s) => {
    const newMappings = { ...s.mappings };
    delete newMappings[payload.id];
    return { mappings: newMappings };
  };
}

export function handleProfileApplicationMapped(payload: {
  mappingId: string;
  profileId: string;
  appId: string;
  createdAt: string;
}): StateMutation {
  log.debug(`Application mapped to profile: ${payload.mappingId}`);
  return (s) => ({
    mappings: {
      ...s.mappings,
      [payload.mappingId]: {
        id: payload.mappingId,
        deploymentProfileId: payload.profileId,
        appId: payload.appId,
        createdAt: payload.createdAt,
        updatedAt: payload.createdAt
      }
    }
  });
}

export function handleProfileMappingUpdated(payload: {
  mappingId: string;
  profileId: string;
  appId: string;
  versionId?: string | null;
  preLaunch?: boolean;
  updatedAt: string;
}): StateMutation {
  log.debug(`Mapping updated: ${payload.mappingId}`);
  return (s) => {
    const existing = s.mappings[payload.mappingId];
    if (!existing) return {};
    return {
      mappings: {
        ...s.mappings,
        [payload.mappingId]: {
          ...existing,
          versionId: payload.versionId,
          preLaunch: payload.preLaunch,
          updatedAt: payload.updatedAt,
        }
      }
    };
  };
}

export function handleProfileApplicationUnmapped(payload: { mappingId: string }): StateMutation {
  log.debug(`Application unmapped from profile: ${payload.mappingId}`);
  return (s) => {
    const newMappings = { ...s.mappings };
    delete newMappings[payload.mappingId];
    return { mappings: newMappings };
  };
}

// =============================================================================
// UPLOAD EVENT HANDLERS
// =============================================================================

export function handleUploadProgress(payload: {
  uploadId: string;
  sentBytes: number;
  totalBytes: number;
  key: string;
  projectId?: string;
  appId?: string;
  replaceVersionId?: string;
  state?: string;
}): StateMutation {
  return (s) => ({
    uploadProgress: {
      ...s.uploadProgress,
      [payload.uploadId]: {
        ...s.uploadProgress[payload.uploadId],
        sent: payload.sentBytes,
        total: payload.totalBytes,
        key: payload.key,
        projectId: payload.projectId,
        appId: payload.appId,
        replaceVersionId: payload.replaceVersionId,
        paused: payload.state === 'paused'
      }
    }
  });
}

export function handleUploadAborted(payload: { uploadId: string }): StateMutation {
  log.debug(`Upload aborted: ${payload.uploadId}`);
  return (s) => {
    const newProgress = { ...s.uploadProgress };
    delete newProgress[payload.uploadId];
    return { uploadProgress: newProgress };
  };
}

export function handleUploadCompleted(payload: { uploadId: string }): StateMutation {
  log.debug(`Upload completed: ${payload.uploadId}`);
  return (s) => {
    const newProgress = { ...s.uploadProgress };
    delete newProgress[payload.uploadId];
    return { uploadProgress: newProgress };
  };
}

export function handleUploadPaused(payload: { uploadId: string }): StateMutation {
  log.debug(`Upload paused: ${payload.uploadId}`);
  return (s) => {
    if (s.uploadProgress[payload.uploadId]) {
      return {
        uploadProgress: {
          ...s.uploadProgress,
          [payload.uploadId]: { ...s.uploadProgress[payload.uploadId], paused: true }
        }
      };
    }
    return {};
  };
}

export function handleUploadResumed(payload: { uploadId: string }): StateMutation {
  log.debug(`Upload resumed: ${payload.uploadId}`);
  return (s) => {
    if (s.uploadProgress[payload.uploadId]) {
      return {
        uploadProgress: {
          ...s.uploadProgress,
          [payload.uploadId]: { ...s.uploadProgress[payload.uploadId], paused: false }
        }
      };
    }
    return {};
  };
}

export function handleUploadInterrupted(payload: { uploadId: string; reason: string }): StateMutation {
  log.debug(`Upload interrupted: ${payload.uploadId} reason: ${payload.reason}`);
  return (s) => {
    if (s.uploadProgress[payload.uploadId]) {
      return {
        uploadProgress: {
          ...s.uploadProgress,
          [payload.uploadId]: { ...s.uploadProgress[payload.uploadId], paused: true }
        }
      };
    }
    return {};
  };
}

// =============================================================================
// CUSTOM HOSTNAME EVENT HANDLERS
// =============================================================================

export function handleCustomHostnameCreated(payload: CustomHostname & { createdByUserId?: string; createdByEmail?: string }): StateMutation {
  log.debug(`Custom hostname created: ${payload.id} ${payload.hostname}`);
  return (s) => ({
    customHostnames: { ...s.customHostnames, [payload.id]: payload }
  });
}

export function handleCustomHostnameUpdated(payload: Partial<CustomHostname> & { id: string }): StateMutation {
  log.debug(`Custom hostname updated: ${payload.id}`);
  return (s) => ({
    customHostnames: {
      ...s.customHostnames,
      [payload.id]: { ...(s.customHostnames[payload.id] || {}), ...payload } as CustomHostname
    }
  });
}

export function handleCustomHostnameDeleted(payload: { id: string; deletedAt?: string }): StateMutation {
  log.debug(`Custom hostname deleted: ${payload.id}`);
  return (s) => {
    const newHostnames = { ...s.customHostnames };
    delete newHostnames[payload.id];
    return { customHostnames: newHostnames };
  };
}

// =============================================================================
// POOL MEMBERSHIP COVERAGE
// =============================================================================

/**
 * Handle AppPoolCoverageChanged — replace coverage entries for each appId
 * in the payload. Empty arrays mean the app lost all coverage; keep the
 * key with `[]` rather than deleting so UIs can distinguish "explicitly
 * uncovered" from "not yet loaded." The snapshot's application chunks
 * populate the initial state; these events carry incremental updates
 * whenever `materializePool` commits on a pool in this org.
 */
export function handleAppPoolCoverageChanged(payload: { coverageByAppId?: Record<string, AppPoolCoverageEntry[]> }): StateMutation {
  const map = payload?.coverageByAppId;
  if (!map || typeof map !== "object") return {};
  return (s) => {
    const next = { ...s.appPoolCoverage };
    for (const appId of Object.keys(map)) {
      const entries = map[appId];
      if (Array.isArray(entries)) next[appId] = entries;
    }
    return { appPoolCoverage: next };
  };
}
